import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import DashboardLayout from '../common/DashboardLayout';
import StateAdminServices from '../../services/StateAdminServices';
import './StatePages.css';

const STATE_NAMES = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  'DC': 'District of Columbia'
};

const SchoolsList = () => {
  const { userrole } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const stateId = localStorage.getItem('stateid') || user.stateid || 'IN';
  const stateName = STATE_NAMES[stateId] || stateId;
  const filterType = searchParams.get('filter') || 'all';

  const [schools, setSchools] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCity, setFilterCity] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    loadSchools();
  }, []);

  const loadSchools = async () => {
    setIsLoading(true);
    try {
      const response = await StateAdminServices.getStateData();
      if (response.success && response.data) {
        setSchools(response.data.schools || []);
      }
    } catch (error) {
      console.error('Error loading schools:', error);
      setSchools([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getFilteredSchools = () => {
    let filtered = schools;

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(s => s.complianceStatus === filterStatus);
    }

    // Apply city filter
    if (filterCity !== 'all') {
      filtered = filtered.filter(s => s.city === filterCity);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(s =>
        s.schoolName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.district?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.schoolName || '').localeCompare(b.schoolName || '');
        case 'compliance':
          return (b.compliancePercentage || 0) - (a.compliancePercentage || 0);
        case 'teachers':
          return (b.totalTeachers || 0) - (a.totalTeachers || 0);
        case 'activity':
          return new Date(b.lastActivityDate || 0).getTime() - new Date(a.lastActivityDate || 0).getTime();
        default:
          return 0;
      }
    });

    return filtered;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'compliant':
        return 'status-badge status-compliant';
      case 'at-risk':
        return 'status-badge status-at-risk';
      case 'non-compliant':
        return 'status-badge status-non-compliant';
      default:
        return 'status-badge';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const exportToCSV = async () => {
    try {
      const filteredSchools = getFilteredSchools();
      const response = await StateAdminServices.exportStateData('csv', { status: filterStatus });
      
      if (response instanceof Blob) {
        const url = window.URL.createObjectURL(response);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${stateId.toLowerCase()}-schools-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  const uniqueCities = [...new Set(schools.map(s => s.city).filter(Boolean))].sort();

  if (isLoading) {
    return (
      <DashboardLayout role={4} layoutType="topnav">
        <div className="state-page">
          <div className="loading-spinner">Loading {stateName} schools...</div>
        </div>
      </DashboardLayout>
    );
  }

  const filteredSchools = getFilteredSchools();

  return (
    <DashboardLayout role={4} layoutType="topnav">
      <div className="state-page">
        <div className="page-header">
          <h1>{stateName} Schools</h1>
          <button className="btn-export" onClick={exportToCSV}>
            📊 Export CSV
          </button>
        </div>

        <div className="filters-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by school name, city, or district..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-controls">
            <div className="filter-group">
              <label>Filter by Status:</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Schools</option>
                <option value="compliant">Compliant Only</option>
                <option value="at-risk">At-Risk Only</option>
                <option value="non-compliant">Non-Compliant Only</option>
              </select>
            </div>

            {filterType === 'city' && (
              <div className="filter-group">
                <label>Filter by City:</label>
                <select
                  value={filterCity}
                  onChange={(e) => setFilterCity(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Cities</option>
                  {uniqueCities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="filter-group">
              <label>Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="filter-select"
              >
                <option value="name">School Name</option>
                <option value="compliance">Compliance %</option>
                <option value="teachers">Number of Teachers</option>
                <option value="activity">Last Activity</option>
              </select>
            </div>
          </div>
        </div>

        <div className="schools-table-container">
          <p className="results-count">Showing {filteredSchools.length} of {schools.length} schools</p>

          <table className="schools-table">
            <thead>
              <tr>
                <th>School Name</th>
                <th>Type</th>
                <th>City</th>
                <th>District</th>
                <th>Teachers</th>
                <th>Certified</th>
                <th>Compliance</th>
                <th>Status</th>
                <th>Last Activity</th>
                <th>Contact</th>
              </tr>
            </thead>
            <tbody>
              {filteredSchools.map((school) => (
                <tr key={school.schoolId}>
                  <td className="school-name">{school.schoolName || 'N/A'}</td>
                  <td>{school.schoolType || 'N/A'}</td>
                  <td>{school.city || 'N/A'}</td>
                  <td>{school.district || 'N/A'}</td>
                  <td>{school.totalTeachers || 0}</td>
                  <td>{school.certifiedTeachers || 0}</td>
                  <td>
                    <div className="compliance-bar-small">
                      <div
                        className={`compliance-fill ${(school.compliancePercentage || 0) >= 80 ? 'fill-compliant' :
                                (school.compliancePercentage || 0) >= 60 ? 'fill-at-risk' :
                                    'fill-non-compliant'
                            }`}
                        style={{ width: `${school.compliancePercentage || 0}%` }}
                      />
                      <span className="compliance-text">{school.compliancePercentage || 0}%</span>
                    </div>
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(school.complianceStatus)}>
                      {school.complianceStatus === 'compliant' && '✅ Compliant'}
                      {school.complianceStatus === 'at-risk' && '⚠️ At-Risk'}
                      {school.complianceStatus === 'non-compliant' && '🚨 Non-Compliant'}
                    </span>
                  </td>
                  <td>{formatDate(school.lastActivityDate)}</td>
                  <td>
                    <div className="contact-info">
                      {school.contactEmail && (
                        <a href={`mailto:${school.contactEmail}`} className="contact-link">
                          📧
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredSchools.length === 0 && (
            <div className="no-results">
              <p>No schools match your search criteria.</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SchoolsList;

